import { evaluate, type FieldError, type FormField } from '@re/form-engine';
import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined';
import AttachFileOutlined from '@mui/icons-material/AttachFileOutlined';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Close from '@mui/icons-material/Close';
import ExpandMore from '@mui/icons-material/ExpandMore';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import PhotoCameraOutlined from '@mui/icons-material/PhotoCameraOutlined';
import {
  Autocomplete,
  Box,
  ButtonBase,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid2 as Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from 'react';
import { PHONE_COUNTRIES } from './phoneCountries';
import { MultiPicker, PickerField, useTouchUI } from './Picker';
import { MapPointInput } from './MapPoint';
import { CURRENCIES, UNITS } from './units';
import type { DynamicFormProps, LocationItem, LocationLevel, Lookups } from './types';

type Lang = 'ar' | 'en';
const T = (lang: Lang, ar: string, en: string) => (lang === 'en' ? en : ar);
const loc = (lang: Lang, l?: { ar?: string; en?: string } | null) => (lang === 'en' && l?.en?.trim() ? l.en : l?.ar ?? '');
const nameOf = (lang: Lang, r?: { name_ar?: string; name_en?: string | null } | null) => (lang === 'en' && r?.name_en?.trim() ? r.name_en : r?.name_ar ?? '');


/** English versions of the form-engine messages (the engine itself speaks Arabic). */
const MSG_EN: Record<string, string> = {
  required: 'This field is required',
  type: 'Invalid value',
  min: 'The value is below the minimum',
  max: 'The value is above the maximum',
  minLength: 'Too short',
  maxLength: 'Too long',
  pattern: 'Invalid format',
  range_order: '"From" must be less than or equal to "To"',
  option: 'Unknown option',
  duplicate: 'Duplicate values',
  email: 'Invalid email address',
  phone: 'Invalid phone number',
  date: 'Invalid date',
  code: 'Invalid value',
  location_depth: 'Please choose the location more precisely',
  map_point: 'The map location is not valid',
  max_files: 'Too many files',
  step: 'The value must be a whole number',
};
const msgOf = (lang: Lang, e: FieldError) => (lang === 'en' ? MSG_EN[e.rule] ?? e.message : e.message);

/**
 * Renders a compiled form schema. Visibility / required / validation come from
 * @re/form-engine — the exact same logic the backend enforces.
 */
export function DynamicForm(props: DynamicFormProps) {
  const { schema, value, onChange, showValidation, errors: serverErrors, disabled } = props;
  const lang: Lang = props.lang ?? 'ar';
  const ev = useMemo(() => evaluate(schema, value), [schema, value]);
  const fields = useMemo(() => [...schema.fields].sort((a, b) => a.order - b.order), [schema]);

  // countries (with their currency): the company's country is the starting point of locations, phones and prices
  const [countries, setCountries] = useState<LocationItem[]>([]);
  useEffect(() => {
    let off = false;
    props.loadLocations('countries').then((c) => !off && setCountries(c)).catch(() => {});
    return () => {
      off = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const dc = props.defaultCountry;
  // the countries this form offers (builder: location field → countries); none listed = all
  const allowedIsos = fields.find((f) => f.type === 'location')?.config.countries ?? [];
  const offered = allowedIsos.length ? countries.filter((c) => c.iso2 && allowedIsos.includes(c.iso2)) : countries;
  const home = offered.find((c) => (typeof dc === 'number' ? c.id === dc : !!dc && c.iso2 === dc)) ?? (offered.length === 1 ? offered[0] : undefined);

  // hidden fields lose their value (spec §6.1-5): drop keys that became invisible
  useEffect(() => {
    const hidden = Object.keys(value).filter((k) => ev.visible[k] === false);
    if (hidden.length) {
      const next = { ...value };
      hidden.forEach((k) => delete next[k]);
      onChange(next);
    }
  }, [ev.visible]); // eslint-disable-line react-hooks/exhaustive-deps

  // dependent choices: a property type that no longer belongs to the chosen category, or features that do not
  // apply to the chosen type, are dropped — otherwise the box looks empty but the stale code is still sent.
  useEffect(() => {
    let next: Record<string, unknown> | null = null;
    for (const f of fields) {
      if (f.type !== 'property_type' && f.type !== 'property_features') continue;
      const cur = (next ?? value)[f.key];
      if (cur === undefined || cur === null || cur === '') continue;
      const allowed = new Set(codeOptions(lang, f, props.lookups, next ?? value, fields).map((o) => o.value));
      if (f.type === 'property_type') {
        if (!allowed.has(cur as string)) {
          next = { ...(next ?? value) };
          delete next[f.key];
        }
      } else if (Array.isArray(cur)) {
        const kept = cur.filter((c) => allowed.has(c as string));
        if (kept.length !== cur.length) {
          next = { ...(next ?? value) };
          if (kept.length) next[f.key] = kept;
          else delete next[f.key];
        }
      }
    }
    if (next) onChange(next);
  }, [value, fields, props.lookups]); // eslint-disable-line react-hooks/exhaustive-deps

  // options with their own condition: a choice that is no longer offered (e.g. the request type changed) is dropped
  useEffect(() => {
    let next: Record<string, unknown> | null = null;
    for (const [key, offered] of Object.entries(ev.options)) {
      const cur = (next ?? value)[key];
      if (cur === undefined || cur === null || cur === '') continue;
      if (Array.isArray(cur)) {
        const kept = cur.filter((c) => offered.includes(c as string));
        if (kept.length === cur.length) continue;
        next = { ...(next ?? value) };
        if (kept.length) next[key] = kept;
        else delete next[key];
      } else if (!offered.includes(cur as string)) {
        next = { ...(next ?? value) };
        delete next[key];
      }
    }
    if (next) onChange(next);
  }, [ev.options]); // eslint-disable-line react-hooks/exhaustive-deps

  const errorOf = (key: string): string | undefined => {
    if (serverErrors?.[key]) return serverErrors[key];
    if (!showValidation) return undefined;
    const e = ev.errors.find((x) => x.field === key || x.field.startsWith(`${key}.`));
    return e ? msgOf(lang, e) : undefined;
  };
  const set = (key: string, v: unknown) => onChange({ ...value, [key]: v });

  return (
    <Grid container columnSpacing={2} rowSpacing={3}>
      {fields.map((f) => {
        if (!ev.visible[f.key]) return null;
        if (props.renderOnly && !props.renderOnly.includes(f.key)) return null;
        const width = f.config.width === 'half' ? 6 : f.config.width === 'third' ? 4 : 12;
        if (f.type === 'section') {
          return (
            <Grid key={f.key} size={12}>
              <Typography variant="h6" sx={{ pt: 1 }}>{loc(lang, f.label)}</Typography>
            </Grid>
          );
        }
        if (f.type === 'info') {
          return (
            <Grid key={f.key} size={12}>
              <Box sx={{ p: 2, borderRadius: 3, bgcolor: (t) => t.palette.tones?.soft ?? 'action.hover', color: 'text.secondary' }}>
                <Typography variant="body2">{loc(lang, f.label)}</Typography>
              </Box>
            </Grid>
          );
        }
        return (
          <Grid key={f.key} size={{ xs: 12, md: width }}>
            <FieldInput
              lang={lang}
              field={ev.options[f.key] ? { ...f, config: { ...f.config, options: (f.config.options ?? []).filter((o) => ev.options[f.key]!.includes(o.value)) } } : f}
              fields={fields}
              value={value[f.key]}
              values={value}
              required={!!ev.required[f.key]}
              error={errorOf(f.key)}
              onChange={(v) => set(f.key, v)}
              lookups={props.lookups}
              loadLocations={props.loadLocations}
              countries={countries}
              home={home}
              uploadFile={props.uploadFile}
              map={props.map}
              disabled={disabled}
            />
          </Grid>
        );
      })}
    </Grid>
  );
}

interface FieldInputProps {
  lang: Lang;
  field: FormField;
  fields: FormField[];
  value: unknown;
  values: Record<string, unknown>;
  required: boolean;
  error?: string;
  onChange: (v: unknown) => void;
  lookups: Lookups;
  loadLocations: DynamicFormProps['loadLocations'];
  countries: LocationItem[];
  /** the company's country */
  home?: LocationItem;
  uploadFile?: DynamicFormProps['uploadFile'];
  map?: DynamicFormProps['map'];
  disabled?: boolean;
}

/**
 * The text next to a number. «currency» follows the country chosen in the form's location field
 * (the company's country until one is chosen) — the same currency the request / property is saved in.
 */
function currencyOf(fields: FormField[], values: Record<string, unknown>, countries: LocationItem[], home?: LocationItem): string | undefined {
  const locField = fields.find((x) => x.type === 'location');
  const chosen = locField ? (values[locField.key] as LocValue | undefined)?.countryId : undefined;
  return (chosen ? countries.find((c) => c.id === chosen) : home)?.currency;
}

function unitText(lang: Lang, key: string | undefined, fields: FormField[], values: Record<string, unknown>, countries: LocationItem[], home?: LocationItem): string | undefined {
  if (!key) return undefined;
  if (key === 'currency') {
    const code = currencyOf(fields, values, countries, home);
    if (!code) return undefined;
    const c = CURRENCIES[code];
    return c ? T(lang, c.ar, c.en) : code;
  }
  const u = UNITS[key];
  return u ? T(lang, u.ar, u.en) : key;
}

function valueOfType(fields: FormField[], values: Record<string, unknown>, type: FormField['type']): unknown {
  const f = fields.find((x) => x.type === type);
  return f ? values[f.key] : undefined;
}

/** Text a searchable list matches against: both languages and the code. */
const searchText = (r: { code?: string; name_ar?: string; name_en?: string | null }) => [r.name_ar, r.name_en, r.code].filter(Boolean).join(' ');

function codeOptions(lang: Lang, field: FormField, lookups: Lookups, values: Record<string, unknown>, fields: FormField[]): Opt[] {
  switch (field.type) {
    case 'request_type': {
      // the request types this form offers (builder); none listed = all of them
      const allow = new Set((field.config.options ?? []).map((o) => o.value));
      return lookups.requestTypes.filter((r) => !allow.size || allow.has(r.code)).map((r) => ({ value: r.code, label: nameOf(lang, r) }));
    }
    case 'property_category':
      return lookups.categories.map((r) => ({ value: r.code, label: nameOf(lang, r) }));
    case 'property_type': {
      const catCode = valueOfType(fields, values, 'property_category');
      const cat = lookups.categories.find((c) => c.code === catCode);
      return lookups.propertyTypes.filter((t) => !cat || t.category_id === cat.id).map((r) => ({ value: r.code, label: nameOf(lang, r), search: searchText(r), ar: r.name_ar }));
    }
    case 'property_features': {
      const ptCode = valueOfType(fields, values, 'property_type');
      const pt = lookups.propertyTypes.find((t) => t.code === ptCode);
      return lookups.features
        .filter((f) => !pt || f.property_type_ids.length === 0 || f.property_type_ids.includes(pt.id))
        .map((r) => ({ value: r.code, label: nameOf(lang, r) }));
    }
    default:
      return (field.config.options ?? []).map((o) => ({ value: o.value, label: loc(lang, o.label), search: [o.label?.ar, o.label?.en, o.value].filter(Boolean).join(' '), ar: o.label?.ar }));
  }
}

/** ar: the Arabic name (used to tell whether a list is alphabetical); name: what to sort and group by when it isn't the label. */
type Opt = { value: string; label: string; search?: string; ar?: string; name?: string };

/** Lists longer than this get a search box (Autocomplete) instead of a plain menu. */
const SEARCH_FROM = 9;
/** Choice lists longer than this open in a sheet / search list instead of tiles. */
const MANY_FROM = 10;

/** Arabic-friendly matching: ignores hamza forms, taa marbuta/haa, alef maqsura/yaa, diacritics, tatweel and case. */
const norm = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u064B-\u065F\u0670\u0640]/g, '')
    .replace(/[\u0622\u0623\u0625\u0671]/g, '\u0627')
    .replace(/\u0629/g, '\u0647')
    .replace(/\u0649/g, '\u064A')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
/** "ال" at the start of a word is optional, so "اردن" finds "الأردن". */
const words = (s: string) => norm(s).split(' ').map((w) => (w.startsWith('\u0627\u0644') && w.length > 3 ? w.slice(2) : w));
function matches(o: Opt, q: string) {
  const qs = words(q).filter(Boolean);
  if (!qs.length) return true;
  const hay = words(`${o.label} ${o.search ?? ''}`).join(' ');
  return qs.every((w) => hay.includes(w));
}

/** Sort key: "ال" and hamza forms ignored, so الأردن sorts under ا and المغرب under م. */
const sortKey = (s: string) => words(s).join(' ');
const collator = (lang: Lang) => new Intl.Collator(lang === 'en' ? 'en' : 'ar', { sensitivity: 'base', numeric: true });
/** First letter heading for a name: ا ب ت … / A B C …; digits and symbols go under #. */
function letterOf(s: string) {
  const ch = sortKey(s).charAt(0);
  return /[\p{L}]/u.test(ch) ? ch.toUpperCase() : '#';
}
/** A list typed in the builder counts as alphabetical when its Arabic names are already in order (the first one may be pinned, e.g. Jordan). */
function looksAlphabetical(opts: Opt[]) {
  const names = opts.slice(1).map((o) => o.ar ?? o.label);
  if (names.length < 3) return false;
  const c = collator('ar');
  const inOrder = (key: (s: string) => string) => names.every((n, i) => i === 0 || c.compare(key(names[i - 1]!), key(n)) <= 0);
  return inOrder((s) => s) || inOrder(sortKey);
}
/** Lists this long or longer show letter headings. */
const GROUP_FROM = 20;
/** 'alpha': always A–Z in the current language. 'auto': A–Z only if the list is already alphabetical, otherwise keep the order it was given. */
function arrange(lang: Lang, opts: Opt[], order: 'alpha' | 'auto') {
  const alpha = order === 'alpha' || looksAlphabetical(opts);
  if (!alpha) return { options: opts, grouped: false };
  const c = collator(lang);
  const sorted = [...opts].sort((a, b) => c.compare(sortKey(a.name ?? a.label), sortKey(b.name ?? b.label)));
  return { options: sorted, grouped: sorted.length >= GROUP_FROM };
}

/** A dropdown with a search box. Keeps the same value shape as the plain menu. */
function SearchSelect(p: { lang: Lang; label: string; value: string; options: Opt[]; order?: 'alpha' | 'auto'; onChange: (v: string | undefined) => void; error?: boolean; disabled?: boolean; helperText?: string; id: string }) {
  const { options, grouped } = useMemo(() => arrange(p.lang, p.options, p.order ?? 'auto'), [p.lang, p.options, p.order]);
  const selected = options.find((o) => o.value === p.value) ?? null;
  // phones: a sheet with the list (the keyboard opens only if the person taps the search box)
  const touch = useTouchUI();
  if (touch) {
    return (
      <PickerField
        id={p.id}
        lang={p.lang}
        label={p.label}
        options={options}
        value={p.value}
        onChange={p.onChange}
        matches={matches}
        groupOf={grouped ? (o) => letterOf(o.name ?? o.label) : undefined}
        error={p.error}
        helperText={p.helperText}
        disabled={p.disabled}
      />
    );
  }
  return (
    <Autocomplete
      id={p.id}
      options={options}
      groupBy={grouped ? (o) => letterOf(o.name ?? o.label) : undefined}
      renderGroup={(g) => <LetterGroup key={g.key} letter={g.group}>{g.children}</LetterGroup>}
      value={selected}
      onChange={(_, o) => p.onChange(o?.value || undefined)}
      getOptionLabel={(o) => o.label}
      isOptionEqualToValue={(a, b) => a.value === b.value}
      filterOptions={(opts, s) => opts.filter((o) => matches(o, s.inputValue))}
      disabled={p.disabled}
      autoHighlight
      openOnFocus
      noOptionsText={T(p.lang, 'لا توجد نتائج', 'No results')}
      renderOption={(props, o) => {
        const { key, ...rest } = props as typeof props & { key: string };
        return (
          <li key={o.value} {...rest}>
            {o.label}
          </li>
        );
      }}
      renderInput={(params) => <TextField {...params} label={p.label} error={p.error} helperText={p.helperText} placeholder={T(p.lang, 'اكتب للبحث…', 'Type to search…')} />}
      fullWidth
    />
  );
}

/** A letter heading that stays on top while its options scroll. */
function LetterGroup({ letter, children }: { letter: string; children: ReactNode }) {
  return (
    <li>
      <Box
        component="div"
        sx={(t) => ({
          position: 'sticky',
          top: -8,
          zIndex: 1,
          px: 2,
          py: 0.5,
          fontSize: 12.5,
          fontWeight: 600,
          color: 'text.secondary',
          bgcolor: t.palette.mode === 'dark' ? t.palette.grey[900] : t.palette.grey[100],
        })}
      >
        {letter}
      </Box>
      <Box component="ul" sx={{ p: 0 }}>{children}</Box>
    </li>
  );
}

/** Question title above grouped inputs (choices, ranges, location, uploads). */
function Question({ label, required, children, error, help, hint }: { label: string; required: boolean; children: ReactNode; error?: string; help?: string; hint?: ReactNode }) {
  return (
    <FormControl fullWidth error={!!error} component="fieldset" sx={{ border: 0, p: 0, m: 0 }}>
      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mb: 1.25 }}>
        <Typography component="legend" variant="subtitle1" sx={{ fontWeight: 500, color: error ? 'error.main' : 'text.primary', p: 0 }}>
          {label}
          {required && <Box component="span" sx={{ color: 'error.main', marginInlineStart: 0.5 }}>*</Box>}
        </Typography>
        {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
      </Stack>
      {children}
      {(error || help) && <FormHelperText sx={{ mt: 1 }}>{error ?? help}</FormHelperText>}
    </FormControl>
  );
}

// ---------------------------------------------------------------- phone with country code
type PhoneCountry = { iso: string; dial: string; ar: string; en: string };
const COUNTRIES: PhoneCountry[] = PHONE_COUNTRIES.map(([iso, dial, ar, en]) => ({ iso, dial, ar, en }));
const DEFAULT_COUNTRY = COUNTRIES[0]!; // Jordan
const flag = (iso: string) => String.fromCodePoint(...[...iso.toUpperCase()].map((c) => 0x1f1a5 + c.charCodeAt(0)));
/** Countries that share a calling code: the one picked when a pasted number doesn't say which. */
const MAIN_FOR_CODE: Record<string, string> = { '1': 'US', '7': 'RU', '44': 'GB', '61': 'AU', '212': 'MA', '262': 'RE', '590': 'GP', '599': 'CW', '47': 'NO', '358': 'FI', '290': 'SH' };

/** "+966501234567" → Saudi Arabia + "501234567". Longest calling code wins. */
function splitPhone(v: string, current: PhoneCountry): { country: PhoneCountry; national: string } {
  const raw = v.trim();
  if (!raw.startsWith('+')) return { country: current, national: raw };
  const digits = raw.slice(1).replace(/\D/g, '');
  if (digits.startsWith(current.dial)) return { country: current, national: digits.slice(current.dial.length) };
  for (let len = 4; len >= 1; len--) {
    const code = digits.slice(0, len);
    const all = COUNTRIES.filter((c) => c.dial === code);
    if (all.length) {
      const pick = all.find((c) => c.iso === MAIN_FOR_CODE[code]) ?? all[0]!;
      return { country: pick, national: digits.slice(len) };
    }
  }
  return { country: current, national: raw };
}

/** The value saved is the full international number, e.g. "+962791234567"; the leading 0 of a local number is dropped. */
function PhoneInput(p: { id: string; lang: Lang; home?: string; label: string; value: string; onChange: (v: unknown) => void; error?: boolean; helperText?: string; placeholder?: string; disabled?: boolean }) {
  const [country, setCountry] = useState<PhoneCountry>(() => splitPhone(p.value, DEFAULT_COUNTRY).country);
  // what the person sees while typing (keeps the leading 0); the saved value is the international number
  const [text, setText] = useState(() => splitPhone(p.value, DEFAULT_COUNTRY).national);
  const sent = useRef(p.value);
  // an empty number starts on the company's country
  useEffect(() => {
    if (!p.home || p.value) return;
    const c = COUNTRIES.find((x) => x.iso === p.home);
    if (c) setCountry(c);
  }, [p.home]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (p.value === sent.current) return; // our own change coming back
    sent.current = p.value;
    const sp = splitPhone(p.value ?? '', country);
    setCountry(sp.country);
    setText(sp.national);
  }, [p.value]); // eslint-disable-line react-hooks/exhaustive-deps
  const send = (v: string | undefined) => {
    sent.current = v ?? '';
    p.onChange(v);
  };
  const emit = (c: PhoneCountry, typed: string) => {
    const t = typed.trim();
    if (!t) return send(undefined);
    if (t.startsWith('+') || t.startsWith('00')) {
      // a full number was typed or pasted: let it choose the country
      const sp = splitPhone('+' + t.replace(/^00/, '').replace(/\D/g, ''), c);
      setCountry(sp.country);
      setText(sp.national);
      return send(sp.national ? `+${sp.country.dial}${sp.national}` : undefined);
    }
    const digits = t.replace(/\D/g, '').replace(/^0+/, '');
    send(digits ? `+${c.dial}${digits}` : undefined);
  };
  const national = text;
  const nameOfC = (c: PhoneCountry) => (p.lang === 'en' ? c.en : c.ar);
  const { options: opts, grouped } = useMemo(
    () => arrange(p.lang, COUNTRIES.map((c) => ({ value: c.iso, label: `${flag(c.iso)} +${c.dial}`, search: `${c.ar} ${c.en} ${c.iso} ${c.dial} +${c.dial}`, ar: c.ar, name: nameOfC(c) })), 'alpha'),
    [p.lang], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const touch = useTouchUI();
  const pickCountry = (iso: string | undefined) => {
    const c = COUNTRIES.find((x) => x.iso === iso);
    if (!c) return;
    setCountry(c);
    if (national) emit(c, national);
  };
  return (
    <Stack direction="row" spacing={1} sx={{ flexDirection: p.lang === 'en' ? 'row' : 'row-reverse' }}>
      {touch ? (
        <PickerField
          id={`${p.id}-country`}
          lang={p.lang}
          label={T(p.lang, 'الدولة', 'Country')}
          title={T(p.lang, 'رمز الدولة', 'Country code')}
          options={opts.map((o) => ({ ...o, label: o.name ?? o.label, hint: `+${COUNTRIES.find((x) => x.iso === o.value)!.dial}`, icon: <Box component="span" sx={{ fontSize: 22 }}>{flag(o.value)}</Box> }))}
          display={(o) => `${flag(o.value)} ${o.hint ?? ''}`}
          value={country.iso}
          onChange={pickCountry}
          matches={matches}
          groupOf={grouped ? (o) => letterOf(o.name ?? o.label) : undefined}
          disabled={p.disabled}
          sx={{ width: 132, flex: 'none', marginInlineStart: '0 !important', marginInlineEnd: 0, '& input': { cursor: 'pointer', caretColor: 'transparent', direction: 'ltr' } }}
        />
      ) : (
      <Autocomplete
        id={`${p.id}-country`}
        options={opts}
        value={opts.find((o) => o.value === country.iso) ?? opts[0]!}
        onChange={(_, o) => pickCountry(o?.value)}
        getOptionLabel={(o) => o.label}
        isOptionEqualToValue={(a, b) => a.value === b.value}
        filterOptions={(all, s) => all.filter((o) => matches(o, s.inputValue))}
        groupBy={grouped ? (o) => letterOf(o.name ?? o.label) : undefined}
        renderGroup={(g) => <LetterGroup key={g.key} letter={g.group}>{g.children}</LetterGroup>}
        disableClearable
        autoHighlight
        disabled={p.disabled}
        noOptionsText={T(p.lang, 'لا توجد نتائج', 'No results')}
        sx={{ width: 148, flex: 'none', marginInlineStart: '0 !important', marginInlineEnd: 0 }}
        slotProps={{ popper: { style: { width: 280 }, placement: p.lang === 'en' ? 'bottom-start' : 'bottom-end' } }}
        renderOption={(props, o) => {
          const { key, ...rest } = props as typeof props & { key: string };
          const c = COUNTRIES.find((x) => x.iso === o.value)!;
          return (
            <li key={o.value} {...rest}>
              <Box component="span" sx={{ fontSize: 18, marginInlineEnd: 1 }}>{flag(c.iso)}</Box>
              <Box component="span" sx={{ flex: 1 }}>{nameOfC(c)}</Box>
              <Box component="span" dir="ltr" sx={{ color: 'text.secondary', marginInlineStart: 1 }}>+{c.dial}</Box>
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={T(p.lang, 'الدولة', 'Country')}
            error={p.error}
            slotProps={{ htmlInput: { ...params.inputProps, dir: 'ltr', 'aria-label': T(p.lang, 'مفتاح الدولة', 'Country code') } }}
          />
        )}
      />
      )}
      <TextField
        id={p.id}
        fullWidth
        label={p.label}
        value={national}
        onChange={(e) => {
          setText(e.target.value);
          emit(country, e.target.value);
        }}
        error={p.error}
        helperText={p.helperText}
        placeholder={p.placeholder ?? (country.iso === 'JO' ? '07X XXX XXXX' : undefined)}
        disabled={p.disabled}
        type="tel"
        autoComplete="tel-national"
        slotProps={{ htmlInput: { dir: 'ltr', inputMode: 'tel' } }}
      />
    </Stack>
  );
}

function FieldInput({ lang, field: f, fields, value, values, required, error, onChange, lookups, loadLocations, countries, home, uploadFile, map, disabled }: FieldInputProps) {
  // money in the location's currency: the title says which one, so «20,000» can't be misread
  const cur = f.config.unit === 'currency' ? currencyOf(fields, values, countries, home) : undefined;
  const curName = cur ? CURRENCIES[cur]?.name : undefined;
  const title = loc(lang, f.label) + (curName ? ` (${T(lang, curName.ar, curName.en)})` : cur ? ` (${cur})` : '');
  const label = title + (required ? ' *' : '');
  const help = error ?? (f.config.help ? loc(lang, f.config.help) : undefined);
  const placeholder = f.config.placeholder ? loc(lang, f.config.placeholder) : undefined;
  const unit = unitText(lang, f.config.unit, fields, values, countries, home);
  const numProps = {
    type: 'number',
    slotProps: {
      input: unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined,
      htmlInput: { min: f.config.validation?.min, max: f.config.validation?.max, step: f.config.validation?.step ?? 'any', inputMode: 'decimal' as const, dir: 'ltr' },
    },
  } as const;
  const toNum = (s: string) => (s === '' ? undefined : Number(s));

  switch (f.type) {
    case 'phone':
      return <PhoneInput id={f.key} lang={lang} home={home?.iso2} label={label} value={(value as string) ?? ''} onChange={onChange} error={!!error} helperText={help} placeholder={placeholder} disabled={disabled} />;
    case 'text':
    case 'email':
      return (
        <TextField
          fullWidth
          label={label}
          value={(value as string) ?? ''}
          onChange={(e) => onChange(e.target.value)}
          error={!!error}
          helperText={help}
          placeholder={placeholder}
          disabled={disabled}
          type={f.type === 'email' ? 'email' : 'text'}
          autoComplete={f.mapsTo === 'contact_name' ? 'name' : f.type === 'email' ? 'email' : undefined}
          slotProps={{ htmlInput: { dir: f.type === 'text' ? undefined : 'ltr', inputMode: f.type === 'email' ? 'email' : undefined } }}
        />
      );
    case 'textarea':
      return <TextField fullWidth multiline minRows={4} label={label} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} error={!!error} helperText={help} placeholder={placeholder} disabled={disabled} />;
    case 'number':
    case 'currency':
      return <TextField fullWidth label={label} value={value ?? ''} onChange={(e) => onChange(toNum(e.target.value))} error={!!error} helperText={help} placeholder={placeholder} disabled={disabled} {...numProps} />;
    case 'number_range': {
      const r = (value as { min?: number; max?: number }) ?? {};
      return (
        <Question label={title} required={required} error={error} help={help}>
          <Stack direction="row" spacing={1.5}>
            <TextField fullWidth label={T(lang, 'من', 'From')} value={r.min ?? ''} onChange={(e) => onChange({ ...r, min: toNum(e.target.value) })} disabled={disabled} error={!!error} {...numProps} />
            <TextField fullWidth label={T(lang, 'إلى', 'To')} value={r.max ?? ''} onChange={(e) => onChange({ ...r, max: toNum(e.target.value) })} disabled={disabled} error={!!error} {...numProps} />
          </Stack>
        </Question>
      );
    }
    case 'date':
      return <TextField fullWidth type="date" label={label} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value)} error={!!error} helperText={help} disabled={disabled} slotProps={{ inputLabel: { shrink: true } }} />;
    case 'date_range': {
      const r = (value as { from?: string; to?: string }) ?? {};
      return (
        <Question label={title} required={required} error={error} help={help}>
          <Stack direction="row" spacing={1.5}>
            <TextField fullWidth type="date" label={T(lang, 'من', 'From')} value={r.from ?? ''} onChange={(e) => onChange({ ...r, from: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} disabled={disabled} error={!!error} />
            <TextField fullWidth type="date" label={T(lang, 'إلى', 'To')} value={r.to ?? ''} onChange={(e) => onChange({ ...r, to: e.target.value })} slotProps={{ inputLabel: { shrink: true } }} disabled={disabled} error={!!error} />
          </Stack>
        </Question>
      );
    }
    case 'boolean':
      return (
        <FormControl error={!!error} fullWidth>
          <Box
            sx={{
              border: 1,
              borderColor: error ? 'error.main' : 'divider',
              borderRadius: 3,
              px: 2,
              py: 0.5,
              bgcolor: 'background.paper',
            }}
          >
            <FormControlLabel
              sx={{ m: 0, width: '100%', justifyContent: 'space-between', '& .MuiFormControlLabel-label': { flex: 1 } }}
              labelPlacement="start"
              control={<Switch checked={value === true} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />}
              label={label}
            />
          </Box>
          {help && <FormHelperText>{help}</FormHelperText>}
        </FormControl>
      );
    case 'property_features': {
      const opts = codeOptions(lang, f, lookups, values, fields);
      return (
        <Question label={title} required={required} error={error} help={help} hint={T(lang, 'اختر كل ما ينطبق', 'Pick everything that applies')}>
          <FeaturesPicker lang={lang} lookups={lookups} available={new Set(opts.map((o) => o.value))} value={Array.isArray(value) ? (value as string[]) : []} onChange={onChange} disabled={disabled} />
        </Question>
      );
    }
    case 'request_type':
    case 'radio':
    case 'checkbox_group': {
      const opts = codeOptions(lang, f, lookups, values, fields);
      const multiple = f.type === 'checkbox_group';
      const arr = Array.isArray(value) ? (value as string[]) : [];
      // a long multi-choice list (e.g. 30+ features): chosen ones as chips, the whole list in a sheet with search
      if (multiple && opts.length > MANY_FROM) {
        return (
          <Question label={title} required={required} error={error} help={help} hint={T(lang, 'يمكنك اختيار أكثر من خيار', 'You can pick more than one')}>
            <MultiPicker
              lang={lang}
              title={title}
              options={opts}
              value={arr}
              onChange={onChange}
              matches={matches}
              error={!!error}
              disabled={disabled}
            />
          </Question>
        );
      }
      // a long single-choice list (e.g. 160 services): one field that opens the list with search
      if (!multiple && opts.length > MANY_FROM) {
        return (
          <Question label={title} required={required} error={error} help={help}>
            <SearchSelect
              id={f.key}
              lang={lang}
              label={T(lang, 'ابحث أو اختر من القائمة', 'Search or pick from the list')}
              value={(value as string) ?? ''}
              options={opts}
              onChange={onChange}
              error={!!error}
              disabled={disabled}
            />
          </Question>
        );
      }
      return (
        <Question
          label={title}
          required={required}
          error={error}
          help={help}
          hint={multiple && opts.length > 1 ? T(lang, 'يمكنك اختيار أكثر من خيار', 'You can pick more than one') : undefined}
        >
          <ChoiceTiles
            big={f.type === 'request_type'}
            multiple={multiple}
            value={multiple ? arr : ((value as string) ?? '')}
            options={opts}
            onChange={onChange}
            error={!!error}
            disabled={disabled}
          />
        </Question>
      );
    }
    case 'dropdown':
    case 'property_category':
    case 'property_type': {
      const opts = codeOptions(lang, f, lookups, values, fields);
      // Long lists get a search box. Property types always do: their number changes with the category, the look must not.
      if (f.type === 'property_type' || (f.type === 'dropdown' && opts.length >= SEARCH_FROM)) {
        return (
          <SearchSelect
            id={f.key}
            lang={lang}
            label={label}
            value={(value as string) ?? ''}
            options={opts}
            order={f.type === 'property_type' ? 'alpha' : 'auto'}
            onChange={onChange}
            error={!!error}
            disabled={disabled}
            helperText={help}
          />
        );
      }
      return (
        <FormControl fullWidth error={!!error} disabled={disabled}>
          <InputLabel id={`${f.key}-label`}>{label}</InputLabel>
          <Select labelId={`${f.key}-label`} label={label} value={(value as string) ?? ''} onChange={(e) => onChange(e.target.value || undefined)}>
            {opts.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
          {help && <FormHelperText>{help}</FormHelperText>}
        </FormControl>
      );
    }
    case 'location':
      return (
        <Question label={title} required={required} error={error} help={help}>
          <LocationInput lang={lang} value={value as LocValue | undefined} onChange={onChange} error={!!error} depth={f.config.depth ?? 'neighborhood'} countries={f.config.countries} loadLocations={loadLocations} homeId={home?.id} disabled={disabled} />
        </Question>
      );
    case 'map_point':
      return (
        <Question label={title} required={required} error={error} help={help}>
          <MapPointInput lang={lang} value={value} onChange={onChange} map={map} disabled={disabled} />
        </Question>
      );
    case 'image_upload':
    case 'file_upload':
      return (
        <Question
          label={title}
          required={required}
          error={error}
          help={help}
          hint={f.config.maxFiles ? T(lang, `حتى ${f.config.maxFiles} ملفات`, `up to ${f.config.maxFiles} files`) : undefined}
        >
          <UploadInput lang={lang} kind={f.type} value={value as string[] | undefined} onChange={onChange} error={!!error} maxFiles={f.config.maxFiles} accept={f.config.accept} uploadFile={uploadFile} disabled={disabled} />
        </Question>
      );
    default:
      return null;
  }
}

/** Large tap targets instead of tiny chips: one tile per option, check mark on the chosen ones. */
/**
 * Property features as one page: a heading per group (التكييف والتدفئة، الأثاث …), the features as chips, and under a chosen
 * feature its details (أجهزة كهربائية → ثلاجة، غسالة …). Choosing a detail keeps its feature; removing a feature removes its details.
 * Long lists get a search box that also finds details («غسالة» shows «أجهزة كهربائية» with it).
 */
export function FeaturesPicker(p: { lang: Lang; lookups: Lookups; available: Set<string>; value: string[]; onChange: (v: unknown) => void; disabled?: boolean }) {
  const { lang } = p;
  const [q, setQ] = useState('');
  const feats = p.lookups.features.filter((f) => p.available.has(f.code));
  const byId = new Map(feats.map((f) => [f.id, f]));
  // a detail whose feature is not offered here stands on its own
  const top = feats.filter((f) => !f.parent_id || !byId.has(f.parent_id));
  const detailsOf = (id: number) => feats.filter((d) => d.parent_id === id);
  const chosen = new Set(p.value);
  const opt = (f: { code: string; name_ar: string; name_en?: string | null }): Opt => ({ value: f.code, label: nameOf(lang, f), search: [f.name_ar, f.name_en, f.code].filter(Boolean).join(' ') });
  const shown = (f: (typeof feats)[number]) => !q.trim() || matches(opt(f), q) || detailsOf(f.id).some((d) => matches(opt(d), q));

  const set = (next: Set<string>) => p.onChange(next.size ? [...next] : undefined);
  const toggleTop = (f: (typeof feats)[number]) => {
    const next = new Set(chosen);
    if (next.has(f.code)) {
      next.delete(f.code);
      detailsOf(f.id).forEach((d) => next.delete(d.code));
    } else next.add(f.code);
    set(next);
  };
  const toggleDetail = (parent: (typeof feats)[number], d: (typeof feats)[number]) => {
    const next = new Set(chosen);
    if (next.has(d.code)) next.delete(d.code);
    else {
      next.add(d.code);
      next.add(parent.code);
    }
    set(next);
  };

  const groups = [...(p.lookups.featureGroups ?? [])];
  const known = new Set(groups.map((g) => g.id));
  const sections = [
    ...groups.map((g) => ({ key: `g${g.id}`, title: nameOf(lang, g), items: top.filter((f) => f.group_id === g.id) })),
    // features with no group (or a group switched off): one last section, untitled when it is the only one
    { key: 'other', title: groups.length ? T(lang, 'أخرى', 'Other') : '', items: top.filter((f) => !f.group_id || !known.has(f.group_id)) },
  ]
    .map((s) => ({ ...s, items: s.items.filter(shown) }))
    .filter((s) => s.items.length);

  const chip = (code: string, label: string, on: boolean, onClick: () => void, small?: boolean) => (
    <ButtonBase
      key={code}
      role="checkbox"
      aria-checked={on}
      disabled={p.disabled}
      onClick={onClick}
      sx={(t) => ({
        px: small ? 1.5 : 1.75,
        minHeight: small ? 36 : 42,
        borderRadius: 999,
        border: 1,
        borderColor: on ? 'primary.main' : 'divider',
        bgcolor: on ? t.palette.tones?.container ?? alpha(t.palette.primary.main, 0.12) : 'background.paper',
        color: on ? t.palette.tones?.onContainer ?? 'primary.main' : 'text.primary',
        fontSize: small ? '0.875rem' : '0.9375rem',
        fontWeight: on ? 600 : 400,
        gap: 0.75,
        transition: 'background-color 120ms, border-color 120ms',
        '&:hover': { borderColor: on ? 'primary.main' : 'text.secondary' },
        '&.Mui-focusVisible': { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
      })}
    >
      {on && <CheckCircle sx={{ fontSize: small ? 16 : 18 }} />}
      {label}
    </ButtonBase>
  );

  const searching = !!q.trim();
  // a group opens by itself when it already holds a choice (back to the step, or a saved draft)
  const [open, setOpen] = useState<Set<string>>(() => new Set(sections.filter((s) => s.items.some((f) => chosen.has(f.code))).map((s) => s.key)));
  const toggleOpen = (k: string) => setOpen((o) => { const n = new Set(o); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const countIn = (items: typeof feats) => items.reduce((a, f) => a + (chosen.has(f.code) ? 1 : 0), 0);
  // one section only (no groups set up): nothing to fold
  const flat = sections.length <= 1;
  const picked = feats.filter((f) => chosen.has(f.code) && (!f.parent_id || !byId.has(f.parent_id)));

  const body = (s: (typeof sections)[number]) => (
    <>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {s.items.map((f) => chip(f.code, nameOf(lang, f), chosen.has(f.code), () => toggleTop(f)))}
      </Box>
      {s.items
        .filter((f) => chosen.has(f.code) && detailsOf(f.id).length)
        .map((f) => (
          <Box key={f.code} sx={(t) => ({ mt: 1.5, p: 1.5, borderRadius: 3, bgcolor: alpha(t.palette.text.primary, 0.04) })}>
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              {nameOf(lang, f)}: {T(lang, 'اختر التفاصيل', 'pick the details')}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ marginInlineStart: 1 }}>{T(lang, 'اختياري', 'Optional')}</Typography>
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
              {detailsOf(f.id).map((d) => chip(d.code, nameOf(lang, d), chosen.has(d.code), () => toggleDetail(f, d), true))}
            </Box>
          </Box>
        ))}
    </>
  );

  return (
    <Stack spacing={2}>
      {feats.length > 12 && (
        <TextField
          size="small"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={T(lang, 'ابحث عن ميزة… (مثلاً: مكيف، غسالة، مسبح)', 'Search… (e.g. AC, washer, pool)')}
          slotProps={{ input: { inputMode: 'search' } }}
        />
      )}
      {!flat && !searching && picked.length > 0 && (
        <Box sx={(t) => ({ p: 1.5, borderRadius: 3, bgcolor: alpha(t.palette.text.primary, 0.04) })}>
          <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
            {T(lang, `اخترت ${picked.length}`, `${picked.length} selected`)}
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {picked.map((f) => chip(f.code, nameOf(lang, f), true, () => toggleTop(f), true))}
          </Box>
        </Box>
      )}
      {flat ? (
        sections.map((s) => <Box key={s.key}>{body(s)}</Box>)
      ) : (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 3, overflow: 'hidden' }}>
          {sections.map((s, i) => {
            const isOpen = searching || open.has(s.key);
            const n = countIn(s.items);
            return (
              <Box key={s.key} sx={{ borderTop: i ? 1 : 0, borderColor: 'divider' }}>
                <ButtonBase
                  onClick={() => toggleOpen(s.key)}
                  disabled={searching}
                  aria-expanded={isOpen}
                  sx={(t) => ({
                    width: '100%',
                    justifyContent: 'flex-start',
                    gap: 1,
                    px: 2,
                    minHeight: 54,
                    textAlign: 'start',
                    '&:hover': { bgcolor: alpha(t.palette.text.primary, 0.03) },
                    '&.Mui-focusVisible': { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: -2 },
                    '&.Mui-disabled': { color: 'inherit' },
                  })}
                >
                  <Typography sx={{ flex: 1, fontWeight: 600, fontSize: '0.9375rem' }}>{s.title || T(lang, 'المميزات', 'Features')}</Typography>
                  {n > 0 ? (
                    <Box component="span" sx={{ px: 1, minWidth: 24, height: 24, borderRadius: 999, bgcolor: 'primary.main', color: 'primary.contrastText', fontSize: 13, fontWeight: 600, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>{n}</Box>
                  ) : null}
                  <ExpandMore sx={{ color: 'text.secondary', transition: 'transform 150ms', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                </ButtonBase>
                <Collapse in={isOpen} timeout={150} unmountOnExit>
                  <Box sx={{ px: 2, pb: 2, pt: 0.5 }}>{body(s)}</Box>
                </Collapse>
              </Box>
            );
          })}
        </Box>
      )}
      {!sections.length && <Typography variant="body2" color="text.secondary">{T(lang, 'لا توجد نتائج', 'No results')}</Typography>}
    </Stack>
  );
}

function ChoiceTiles(p: {
  options: { value: string; label: string }[];
  value: string | string[];
  multiple?: boolean;
  big?: boolean;
  onChange: (v: unknown) => void;
  error?: boolean;
  disabled?: boolean;
}) {
  const arr = Array.isArray(p.value) ? p.value : [];
  const active = (code: string) => (p.multiple ? arr.includes(code) : p.value === code);
  const click = (code: string) => {
    if (p.multiple) p.onChange(arr.includes(code) ? arr.filter((x) => x !== code) : [...arr, code]);
    else p.onChange(p.value === code ? undefined : code);
  };
  return (
    <Box
      role={p.multiple ? 'group' : 'radiogroup'}
      sx={{
        display: 'grid',
        gap: 1,
        gridTemplateColumns: p.big
          ? { xs: 'repeat(2, minmax(0, 1fr))', sm: `repeat(${Math.min(4, Math.max(2, p.options.length))}, minmax(0, 1fr))` }
          : { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))' },
      }}
    >
      {p.options.map((o) => {
        const on = active(o.value);
        return (
          <ButtonBase
            key={o.value}
            role={p.multiple ? 'checkbox' : 'radio'}
            aria-checked={on}
            disabled={p.disabled}
            onClick={() => click(o.value)}
            sx={(t) => ({
              position: 'relative',
              justifyContent: 'flex-start',
              textAlign: 'start',
              minHeight: p.big ? 64 : 48,
              px: 1.75,
              py: 1.25,
              gap: 1,
              borderRadius: 3,
              border: 1,
              borderColor: on ? 'primary.main' : p.error ? 'error.main' : 'divider',
              bgcolor: on ? t.palette.tones?.container ?? alpha(t.palette.primary.main, 0.12) : 'background.paper',
              color: on ? t.palette.tones?.onContainer ?? 'primary.main' : 'text.primary',
              fontSize: p.big ? '1rem' : '0.9375rem',
              fontWeight: on ? 600 : 400,
              lineHeight: 1.35,
              boxShadow: on ? `inset 0 0 0 1px ${t.palette.primary.main}` : 'none',
              transition: 'background-color 120ms, border-color 120ms',
              '&:hover': { borderColor: on ? 'primary.main' : 'text.secondary' },
              '&.Mui-focusVisible': { outline: `2px solid ${t.palette.primary.main}`, outlineOffset: 2 },
            })}
          >
            <Box component="span" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{o.label}</Box>
            <CheckCircle sx={{ fontSize: 20, color: 'primary.main', opacity: on ? 1 : 0, transition: 'opacity 120ms', flexShrink: 0 }} />
          </ButtonBase>
        );
      })}
    </Box>
  );
}

type LocValue = { countryId?: number; governorateId?: number; areaId?: number; neighborhoodId?: number };
const LEVELS: { key: keyof LocValue; level: LocationLevel; ar: string; en: string; parent?: keyof LocValue }[] = [
  { key: 'countryId', level: 'countries', ar: 'الدولة', en: 'Country' },
  { key: 'governorateId', level: 'governorates', ar: 'المحافظة', en: 'Governorate', parent: 'countryId' },
  { key: 'areaId', level: 'areas', ar: 'المنطقة', en: 'Area', parent: 'governorateId' },
  { key: 'neighborhoodId', level: 'neighborhoods', ar: 'الحي', en: 'Neighborhood', parent: 'areaId' },
];

/** What each level is called where it is not «governorate / area / neighborhood». */
const LEVEL_NAMES: Record<string, Partial<Record<keyof LocValue, [string, string]>>> = {
  SA: { governorateId: ['المنطقة', 'Region'], areaId: ['المدينة', 'City'] },
  AE: { governorateId: ['الإمارة', 'Emirate'], areaId: ['المنطقة', 'Area'] },
};

function LocationInput(p: { lang: Lang; value?: LocValue; onChange: (v: unknown) => void; error?: boolean; depth: string; countries?: string[]; loadLocations: DynamicFormProps['loadLocations']; homeId?: number; disabled?: boolean }) {
  const v = p.value ?? {};
  const depthIdx = ['country', 'governorate', 'area', 'neighborhood'].indexOf(p.depth);
  const levels = LEVELS.slice(0, depthIdx + 1);
  const [opts, setOpts] = useState<Record<string, LocationItem[]>>({});

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const next: Record<string, LocationItem[]> = {};
      for (const l of levels) {
        const parentId = l.parent ? v[l.parent] : undefined;
        if (l.parent && !parentId) break;
        const items = await p.loadLocations(l.level, parentId).catch(() => [] as LocationItem[]);
        // only the countries this form offers
        next[l.key] = l.key === 'countryId' && p.countries?.length ? items.filter((c) => c.iso2 && p.countries!.includes(c.iso2)) : items;
      }
      if (!cancelled) setOpts(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [v.countryId, v.governorateId, v.areaId, p.depth, p.countries?.join()]); // eslint-disable-line react-hooks/exhaustive-deps

  // one country → chosen for the person; several → the company's country is chosen first (they can change it)
  useEffect(() => {
    const c = opts.countryId;
    if (!c?.length) return;
    if (v.countryId && !c.some((x) => x.id === v.countryId)) return p.onChange({}); // a country this form no longer offers (old draft)
    if (v.countryId) return;
    const pick = c.length === 1 ? c[0] : c.find((x) => x.id === p.homeId);
    if (pick) p.onChange({ countryId: pick.id });
  }, [opts.countryId, p.homeId]); // eslint-disable-line react-hooks/exhaustive-deps

  const change = (idx: number, id: number | undefined) => {
    const next: LocValue = {};
    levels.forEach((l, i) => {
      if (i < idx) next[l.key] = v[l.key];
      else if (i === idx && id) next[l.key] = id;
    });
    p.onChange(next);
  };

  // a single country is chosen automatically → do not show it; a level with nothing under the chosen parent is hidden too
  const shown = levels.filter((l) => {
    if (l.key === 'countryId') return (opts.countryId?.length ?? 0) > 1;
    return !(l.parent && v[l.parent] && opts[l.key]?.length === 0);
  });
  const iso = opts.countryId?.find((c) => c.id === v.countryId)?.iso2;
  const names = (iso && LEVEL_NAMES[iso]) || {};

  return (
    <Grid container spacing={1.5}>
      {shown.map((l) => {
        const i = levels.indexOf(l);
        const n = names[l.key];
        const label = n ? T(p.lang, n[0], n[1]) : T(p.lang, l.ar, l.en);
        return (
          <Grid key={l.key} size={{ xs: 12, sm: 12 / Math.max(1, Math.min(shown.length, 3)) }}>
            <SearchSelect
              id={l.key}
              lang={p.lang}
              label={label}
              value={v[l.key] ? String(v[l.key]) : ''}
              options={(opts[l.key] ?? []).map((o) => ({ value: String(o.id), label: nameOf(p.lang, o), search: [o.name_ar, o.name_en].filter(Boolean).join(' '), ar: o.name_ar }))}
              order="alpha"
              onChange={(id) => change(i, id ? Number(id) : undefined)}
              error={p.error && !v[l.key]}
              disabled={p.disabled || (l.parent ? !v[l.parent] : false)}
            />
          </Grid>
        );
      })}
    </Grid>
  );
}

function UploadInput(p: {
  lang: Lang;
  kind: 'image_upload' | 'file_upload';
  value?: string[];
  onChange: (v: unknown) => void;
  error?: boolean;
  maxFiles?: number;
  accept?: string[];
  uploadFile?: DynamicFormProps['uploadFile'];
  disabled?: boolean;
}) {
  const ids = p.value ?? [];
  const [busy, setBusy] = useState(0);
  const [meta, setMeta] = useState<Record<string, { name: string; preview?: string }>>({});
  const [err, setErr] = useState<string>();
  const filesRef = useRef<HTMLInputElement>(null);
  const cameraRef = useRef<HTMLInputElement>(null);
  const full = p.maxFiles !== undefined && ids.length >= p.maxFiles;
  const locked = p.disabled || !p.uploadFile || full || busy > 0;
  const accept = p.accept?.join(',');
  const images = p.kind === 'image_upload';

  useEffect(() => () => Object.values(meta).forEach((m) => m.preview && URL.revokeObjectURL(m.preview)), []); // eslint-disable-line react-hooks/exhaustive-deps

  const pick = async (files: FileList | null) => {
    if (!files || !p.uploadFile) return;
    setErr(undefined);
    const room = p.maxFiles !== undefined ? p.maxFiles - ids.length : files.length;
    // only what this field accepts (the picker can still be switched to «all files»)
    const all = Array.from(files);
    const okType = (f: File) => !p.accept?.length || p.accept.includes(f.type);
    const wrong = all.filter((f) => !okType(f));
    const list = all.filter(okType).slice(0, room);
    if (wrong.length) {
      const kinds = (p.accept ?? []).map((m) => (m === 'application/pdf' ? 'PDF' : m.replace('image/', '').toUpperCase())).join(T(p.lang, '، ', ', '));
      setErr(T(p.lang, `نوع الملف غير مسموح هنا. المسموح: ${kinds}`, `This file type is not allowed here. Allowed: ${kinds}`));
      if (!list.length) return;
    }
    setBusy(list.length);
    const added: string[] = [];
    try {
      for (const file of list) {
        const id = await p.uploadFile(file);
        added.push(id);
        setMeta((m) => ({ ...m, [id]: { name: file.name, preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : undefined } }));
        setBusy((n) => n - 1);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : T(p.lang, 'فشل رفع الملف', 'Upload failed'));
    } finally {
      setBusy(0);
      if (added.length) p.onChange([...ids, ...added]);
    }
  };
  const onFiles = (e: ChangeEvent<HTMLInputElement>) => {
    void pick(e.target.files);
    e.target.value = '';
  };

  return (
    <Box>
      <Box sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(5, minmax(0, 1fr))' } }}>
        {ids.map((id) => {
          const m = meta[id];
          return (
            <Box key={id} sx={{ position: 'relative', aspectRatio: '1', borderRadius: 3, overflow: 'hidden', border: 1, borderColor: 'divider', bgcolor: 'action.hover' }}>
              {m?.preview ? (
                <Box component="img" src={m.preview} alt={m.name} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              ) : (
                <Stack alignItems="center" justifyContent="center" spacing={0.5} sx={{ height: '100%', p: 1, color: 'text.secondary' }}>
                  <InsertDriveFileOutlined />
                  <Typography variant="caption" noWrap sx={{ maxWidth: '100%' }}>{m?.name ?? id.slice(0, 8)}</Typography>
                </Stack>
              )}
              {!p.disabled && (
                <IconButton
                  size="small"
                  aria-label={T(p.lang, 'إزالة', 'Remove')}
                  onClick={() => p.onChange(ids.filter((x) => x !== id))}
                  sx={{ position: 'absolute', top: 4, insetInlineEnd: 4, bgcolor: 'rgba(0,0,0,.55)', color: '#fff', '&:hover': { bgcolor: 'rgba(0,0,0,.7)' }, p: 0.4 }}
                >
                  <Close sx={{ fontSize: 16 }} />
                </IconButton>
              )}
            </Box>
          );
        })}
        {Array.from({ length: busy }).map((_, i) => (
          <Box key={`b${i}`} sx={{ aspectRatio: '1', borderRadius: 3, border: 1, borderColor: 'divider', display: 'grid', placeItems: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ))}
      </Box>

      {!full && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: ids.length || busy ? 1.5 : 0 }}>
          <ButtonBase
            disabled={locked}
            onClick={() => filesRef.current?.click()}
            sx={(t) => ({
              flex: 1,
              minHeight: 88,
              borderRadius: 3,
              border: '1.5px dashed',
              borderColor: p.error ? 'error.main' : t.palette.tones?.outline ?? 'divider',
              bgcolor: t.palette.tones?.soft ?? 'action.hover',
              flexDirection: 'column',
              gap: 0.5,
              color: 'primary.main',
              '&.Mui-disabled': { opacity: 0.5 },
            })}
          >
            {images ? <AddPhotoAlternateOutlined /> : <AttachFileOutlined />}
            <Typography variant="body2" fontWeight={500}>
              {images ? T(p.lang, 'اختر صوراً من جهازك', 'Choose photos') : T(p.lang, 'اختر ملفات', 'Choose files')}
            </Typography>
          </ButtonBase>
          {images && (
            <ButtonBase
              disabled={locked}
              onClick={() => cameraRef.current?.click()}
              sx={(t) => ({
                display: { xs: 'flex', md: 'none' },
                minHeight: 56,
                flex: { sm: '0 0 160px' },
                borderRadius: 3,
                border: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
                gap: 1,
                color: t.palette.text.primary,
                '&.Mui-disabled': { opacity: 0.5 },
              })}
            >
              <PhotoCameraOutlined />
              <Typography variant="body2" fontWeight={500}>{T(p.lang, 'التقط صورة', 'Take a photo')}</Typography>
            </ButtonBase>
          )}
        </Stack>
      )}
      {!p.uploadFile && <FormHelperText>{T(p.lang, 'رفع الملفات غير متاح حالياً', 'Uploads are not available right now')}</FormHelperText>}
      {err && <FormHelperText error>{err}</FormHelperText>}
      <input ref={filesRef} hidden type="file" multiple accept={accept} onChange={onFiles} />
      {images && <input ref={cameraRef} hidden type="file" accept={accept ?? 'image/*'} capture="environment" onChange={onFiles} />}
    </Box>
  );
}
